import React, { useMemo } from 'react';
import { Formik } from 'formik';
import { Button, Col, Modal, Row } from 'react-bootstrap';
import * as yup from 'yup';
import * as R from 'ramda';
import SelectField from 'Shared/form/SelectField';
import ReferralMedicationSelect from './ReferralMedicationSelect';
import useCreateReferralActivityMutation from 'App/hooks/useCreateReferralActivityMutation';
import toast from 'Shared/toast';
import { referralActivityPropType, referralModalPropType } from './types';
import FormButtonGroup from '../styles/FormButtonGroup';
import hasLength from '../../utils/hasLength';
import SubmitButton from 'Shared/form/SubmitButton';
import { formatDate } from '../PatientProfile/PatientProfile.utils';

const validationSchema = yup.object().shape({
    type: yup.object().required('Please select an activity.').nullable(),
    medication: yup.array(),
    paActivityId: yup
        .object()
        .when('type', {
            is: (type) => type?.id === 3,
            then: yup.object().required('Please select an Upheld Prior Authorization to Appeal.'),
        })
        .nullable(),
    paActivityIdRenewal: yup
        .object()
        .when('type', {
            is: (type) => type?.id === 4,
            then: yup.object().required('Please select an approved Prior Authorization activity to Renew.'),
        })
        .nullable(),
    faActivityRenewal: yup
        .object()
        .when('type', {
            is: (type) => type?.id === 5,
            then: yup.object().required('Please select an approved Financial Assistance activity to Renew.'),
        })
        .nullable(),
});

const defaultProps = {};

const propTypes = {
    ...referralModalPropType,
    ...referralActivityPropType,
};

const REFERRAL_ACTIVITY_TYPES = Object.freeze({
    0: { value: 0, label: 'Benefits Investigation' },
    1: { value: 1, label: 'Prior Authorization' },
    2: { value: 2, label: 'Financial Assistance' },
    3: { value: 3, label: 'Appeal' },
    4: { value: 4, label: 'Prior Authorization Renewal' },
    5: { value: 5, label: 'Financial Assistance Renewal' },
});

const initialValues = {
    medication: [],
    paActivityId: null,
    type: null,
};

const AddReferralActivity = ({ show, onHide, patientId, referralId, activities }) => {
    const createReferralActivityMutation = useCreateReferralActivityMutation();
    const activitiesMap = useMemo(() => {
        return (activities || []).reduce(
            (result, value) => {
                const next = result;
                if (['Pending', 'New'].includes(value.status)) {
                    next[value.type] = next[value.type].concat((value.medications || []).map((it) => it.medicationId));
                    // For appeals, get the Prior Auth activity ids
                    if (value.type === 3) {
                        next[value.type].push(value.details.paActivityId);
                    }
                    if (value.type === 5) {
                        next[value.type].push(value.details.faActivityId);
                    }
                }
                return next;
            },
            { 0: [], 1: [], 2: [], 3: [], 4: [], 5: [] }
        );
    }, [activities]);

    const deniedPAActivities = useMemo(() => {
        if (hasLength(activities)) {
            return activities.filter(
                R.allPass([
                    R.propEq(1, 'type'),
                    R.anyPass([R.propEq('Denied', 'status'), R.propEq('Appealed - Upheld', 'status')]),
                ])
            );
        }
        return [];
    }, [activities]);

    const validPriorAuthorizations = useMemo(() => {
        return R.reject(R.compose(R.includes(R.__, activitiesMap[3]), R.prop('id')))(deniedPAActivities);
    }, [activitiesMap, deniedPAActivities]);

    const activitiesList = useMemo(() => {
        return R.compose(
            R.values,
            R.ifElse(R.always(hasLength(validPriorAuthorizations)), R.assoc(3, REFERRAL_ACTIVITY_TYPES[3]), R.identity),
            R.compose(R.assoc(4, REFERRAL_ACTIVITY_TYPES[4]), R.assoc(5, REFERRAL_ACTIVITY_TYPES[5])),
            R.pick([0, 1, 2])
        )(REFERRAL_ACTIVITY_TYPES);
    }, [validPriorAuthorizations]);

    const approvedPAActivityList = (activities || []).reduce((result, value) => {
        if (value.type === 1 && value.status === 'Approved') {
            const medicationName = value.medications && value.medications[0]?.medication?.name;

            result.push({
                id: value.id,
                name: `PA ${value.type === 4 ? 'Renewal ' : ''}approved on  ${formatDate(
                    value.statusDate
                )} (${medicationName})`,
            });
        }
        return result;
    }, []);

    const approvedFAActivityList = (activities || []).reduce((result, value) => {
        if (value.type === 2 && value.status === 'Approved') {
            const medicationName = value.medications && value.medications[0]?.medication?.name;

            result.push({
                id: value.id,
                name: `FA ${value.type === 5 ? 'Renewal ' : ''}approved on ${formatDate(
                    value.statusDate
                )} (${medicationName})`,
            });
        }
        return result;
    }, []);

    return (
        <Modal show={show} onHide={() => onHide(false)} backdrop="static" className="referral-add-activity">
            <Modal.Header>
                <Modal.Title role="heading">Add Activity</Modal.Title>
            </Modal.Header>
            <Formik
                initialValues={initialValues}
                validationSchema={validationSchema}
                onSubmit={(values, { setSubmitting }) => {
                    setSubmitting(true);
                    createReferralActivityMutation
                        .mutateAsync({
                            patientId,
                            referralId,
                            body: {
                                details: {
                                    ...(values.type.value === 3 ? { pa_activity_id: values.paActivityId.id } : {}),
                                    ...(values.type.value === 4
                                        ? { pa_activity_id_renewal: values.paActivityIdRenewal.id }
                                        : {}),
                                    ...(values.type.value === 5 ? { fa_activity_id: values.faActivityRenewal.id } : {}),
                                },
                                medication: values.medication.map((it) => ({ medication_id: it.id })),
                                type: values.type.value,
                            },
                        })
                        .then(() => {
                            toast.success('The referral activity was created successfully.');
                        })
                        .finally(() => {
                            setSubmitting(false);
                            onHide(true);
                        });
                }}
            >
                {({ isSubmitting, handleSubmit, submitForm, isValid, values }) => {
                    return (
                        <form onSubmit={handleSubmit} noValidate>
                            <Modal.Body>
                                <Row>
                                    <Col md={12}>
                                        <SelectField
                                            options={activitiesList}
                                            isClearable
                                            required
                                            label="Select an Activity"
                                            fieldName="type"
                                        />
                                    </Col>
                                    {values.type?.value === 4 && (
                                        <Col md={12}>
                                            <SelectField
                                                options={approvedPAActivityList}
                                                isClearable
                                                required
                                                label="Approved Prior Authorization to Renew"
                                                fieldName="paActivityIdRenewal"
                                                getOptionValue={R.prop('id')}
                                                getOptionLabel={R.prop('name')}
                                            />
                                        </Col>
                                    )}
                                    {values.type?.value === 5 && (
                                        <Col md={12}>
                                            <SelectField
                                                options={approvedFAActivityList}
                                                isClearable
                                                required
                                                label="Approved Financial Assistance to Renew"
                                                fieldName="faActivityRenewal"
                                                getOptionValue={R.prop('id')}
                                                getOptionLabel={R.prop('name')}
                                            />
                                        </Col>
                                    )}
                                    <Col md={12}>
                                        <ReferralMedicationSelect
                                            patientId={patientId}
                                            referralId={referralId}
                                            activities={activities}
                                            activitiesMap={activitiesMap}
                                            validPriorAuthorizations={validPriorAuthorizations}
                                        />
                                    </Col>
                                </Row>
                            </Modal.Body>
                            <Modal.Footer>
                                <FormButtonGroup>
                                    <Button
                                        variant="outline-secondary"
                                        disabled={isSubmitting}
                                        onClick={() => {
                                            return onHide(false);
                                        }}
                                    >
                                        Cancel
                                    </Button>
                                    <SubmitButton
                                        onClick={submitForm}
                                        id="add-referral-activity-modal-submit"
                                        disabled={!isValid || isSubmitting}
                                    >
                                        Save
                                    </SubmitButton>
                                </FormButtonGroup>
                            </Modal.Footer>
                        </form>
                    );
                }}
            </Formik>
        </Modal>
    );
};

AddReferralActivity.propTypes = propTypes;
AddReferralActivity.defaultProps = defaultProps;

export default AddReferralActivity;
